/**
 * 4 — Marker & keying: override the auto marker colour + tolerance.
 *
 * Extracted from `MaskEditor.tsx` (WS-J J-12). Preserved verbatim: the eyedropper, WS-I's
 * `guide colour (auto) — pick any other colour` label and its live "Keyed %" readout (which is live on
 * the DEFAULT path, not only after a pick), and the on-device reassurance note.
 */
import { useState } from "react";
import { Icon } from "../../ui/Icon.js";
import { InfoHint } from "../../ui/Tooltip.js";
import { SEGMENTATION_HELP, valueHelpText } from "../../ui/valueHelp.js";
import { useOptionalServices } from "../../app/context.js";
import { useSessionStore } from "../../stores/session.js";
import { cacheEpoch, writeCache } from "../../stores/cache.js";
import { CACHE_KEYS } from "../../stores/sessionWarmup.js";
import { hexToRgb, rgbToHexColor } from "../pixels.js";
import type { GuideKeyOptions } from "../chromaKey.js";

export interface MarkerKeyingCardProps {
  locked?: boolean;
  chromaReady: boolean;
  markerColor: [number, number, number] | null;
  setMarkerColor: (c: [number, number, number] | null) => void;
  markerTol: number;
  setMarkerTol: (v: number) => void;
  markerEyedrop: boolean;
  setMarkerEyedrop: React.Dispatch<React.SetStateAction<boolean>>;
  keyedPct: number | null;
  pickNotice: string | null;
  guideKey: Required<GuideKeyOptions>;
  setGuideKey: React.Dispatch<React.SetStateAction<Required<GuideKeyOptions>>>;
  /** The host's camera shading fix, under the key controls. */
  cameraShading?: React.ReactNode;
}

/**
 * Owner, 2026-10-14: "add tolerance inside the editor … so that I can play there and fix the
 * value … then I can save it in the main". The key re-keys as a slider moves; a super-admin
 * then saves the three values as the default for every device.
 */
function GuideKeyControls({ locked, guideKey, setGuideKey }: Pick<MarkerKeyingCardProps, "locked" | "guideKey" | "setGuideKey">) {
  const client = useOptionalServices()?.client;
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const [saving, setSaving] = useState<"idle" | "saving" | "saved" | "failed">("idle");
  const set = <K extends keyof GuideKeyOptions>(key: K, value: Required<GuideKeyOptions>[K]) => {
    setGuideKey((g) => ({ ...g, [key]: value }));
    setSaving("idle");
  };
  const saveForEveryone = async () => {
    setSaving("saving");
    const epoch = cacheEpoch();
    try {
      // Read the platform values first, so this changes the three key values and nothing else.
      if (!client) throw new Error("no cloud");
      const current = (await client.getSettings()).segmenter_calibration ?? {};
      const updated = await client.updateSettings({ segmenter_calibration: {
        ...current, key_method: guideKey.method, key_by_purity: guideKey.method === "purity", key_tolerance: guideKey.tolerance,
        key_noise_floor: guideKey.noiseFloor, key_snap_low: guideKey.snapLow, key_snap_high: guideKey.snapHigh,
        key_flat_sample: guideKey.flatSample,
        key_purity_start: guideKey.start, key_purity_full: guideKey.full, key_dark_keep: guideKey.darkKeep,
        key_softness: guideKey.softness, key_colour_removal: guideKey.colour,
      } });
      writeCache(CACHE_KEYS.settings, updated, epoch);
      setSaving("saved");
    } catch {
      setSaving("failed");
    }
  };
  const MIN: Partial<Record<keyof GuideKeyOptions, number>> = { snapHigh: 0.8, flatSample: 0.3 };
  const slider = (key: Exclude<keyof GuideKeyOptions, "method">, label: string, help: keyof typeof SEGMENTATION_HELP, max: number, step: number, fmt: (v: number) => string) => (
    <label className="mask-editor__chroma-tol">
      <span data-testid={`help-guide-${key}`}><InfoHint label={`About ${label}`} text={valueHelpText(SEGMENTATION_HELP[help])} /></span>{" "}
      {label} {fmt(guideKey[key])}
      <input type="range" min={MIN[key] ?? 0} max={max} step={step} value={guideKey[key]} disabled={locked}
        onChange={(e) => set(key, Number(e.target.value))} aria-label={label} data-testid={`guide-${key}`} />
    </label>
  );
  const pct = (v: number) => `${Math.round(v * 100)}%`;
  return (
    <div className="mask-editor__calibration" data-testid="guide-key-controls">
      <div className="mask-editor__chroma-row" role="group" aria-label="Key method">
        {(["bulk", "purity", "spread"] as const).map((m) => (
          <button key={m} type="button" className={`btn btn--sm${guideKey.method === m ? " is-active" : ""}`}
            aria-pressed={guideKey.method === m} disabled={locked} onClick={() => set("method", m)}
            data-testid={`guide-method-${m}`}>
            {m === "bulk" ? "Colour to alpha" : m === "purity" ? "Purity" : "Spread"}
          </button>
        ))}
      </div>
      {guideKey.method === "bulk" ? (
        <>
          {slider("noiseFloor", "Noise floor", "key_noise_floor", 20, 1, (v) => String(v))}
          {slider("flatSample", "Flat sample", "key_flat_sample", 0.9, 0.05, pct)}
          {slider("snapLow", "Transparent snap", "key_snap_low", 0.2, 0.005, pct)}
          {slider("snapHigh", "Opaque snap", "key_snap_high", 1, 0.005, pct)}
        </>
      ) : guideKey.method === "purity" ? (
        <>
          {slider("start", "Keying starts", "key_purity_start", 1, 0.01, pct)}
          {slider("full", "Fully transparent at", "key_purity_full", 1, 0.01, pct)}
          {slider("darkKeep", "Keep dark magenta", "key_dark_keep", 1, 0.05, pct)}
        </>
      ) : slider("tolerance", "Tolerance", "key_tolerance", 1, 0.05, pct)}
      {slider("softness", "Edge softness", "key_softness", 4, 1, (v) => `${v}px`)}
      {slider("colour", "Colour removal", "key_colour_removal", 1, 0.05, pct)}
      {isSuper && client && (
        <div className="mask-editor__chroma-row">
          <button type="button" className="btn btn--sm" disabled={locked || saving === "saving"}
            onClick={() => void saveForEveryone()} data-testid="guide-save-default">
            {saving === "saving" ? "Saving…" : "Save as default for everyone"}
          </button>
          {saving === "saved" && <span className="faint" role="status" data-testid="guide-saved">Saved. Every device takes these values when it is next in view.</span>}
          {saving === "failed" && <span className="faint" role="status" data-testid="guide-save-failed">The values were not saved. Check the connection and try again.</span>}
        </div>
      )}
    </div>
  );
}

export function MarkerKeyingCard(p: MarkerKeyingCardProps) {
  const {
    locked, chromaReady, markerColor, setMarkerColor, markerTol,
    setMarkerTol, markerEyedrop, setMarkerEyedrop, keyedPct, pickNotice, guideKey, setGuideKey, cameraShading,
  } = p;
  return (
    <>
        {/* 4 — Marker & keying: override the auto marker colour + tolerance when the generated
            marker drifted from #FF00FF, plus the on-device reassurance note. */}
        {/* WS-Z6 / R12: the card is an accordion BODY now — the head, its marker and the
            open key belong to the rail. `data-testid` and every control are unchanged. */}
        <div className="mask-editor__section-body" data-testid="section-marker">
          {/* Marker colour (magenta print-area marker): the keyer targets pure magenta by
              default; if the generated marker DRIFTED from the #FF00FF code, pick the actual
              colour here (eyedropper or swatch) — the keying process is otherwise unchanged. */}
          <div className="mask-editor__chroma-row" data-testid="marker-color-row">
            <button
              type="button"
              className={`btn btn--sm${markerEyedrop ? " is-active" : ""}`}
              onClick={() => setMarkerEyedrop((v) => !v)}
              disabled={locked || !chromaReady}
              aria-pressed={markerEyedrop}
              title="Eyedropper — click the image to pick the marker colour (use if it drifted from the guide colour)"
              data-testid="marker-eyedropper"
            >
              {markerEyedrop ? "Click the marker…" : <><Icon name="target" size={16} /> Pick marker colour</>}
            </button>
            <input
              type="color"
              value={markerColor ? rgbToHexColor(markerColor) : "#ff00ff"}
              onChange={(e) => {
                const c = hexToRgb(e.target.value);
                if (c) setMarkerColor(c);
              }}
              disabled={locked}
              aria-label="Marker colour"
              data-testid="marker-color"
              className="mask-editor__marker-swatch"
            />
            <span className="mask-editor__marker-current">
              {markerColor ? rgbToHexColor(markerColor) : "guide colour (auto) — pick any other colour"}
            </span>
            <label className="mask-editor__chroma-tol" title="How close a pixel must be to the picked colour to count as the marker">
              Tolerance {markerTol === 0 ? "auto" : markerTol}
              <input
                type="range"
                min={0}
                max={255}
                value={markerTol}
                onChange={(e) => setMarkerTol(Number(e.target.value))}
                disabled={locked || !markerColor}
                title={!markerColor ? "Pick a marker colour first (use the eyedropper)" : "How close a pixel must be to the picked colour to count as the marker"}
                aria-label="Marker colour tolerance"
                data-testid="marker-tolerance"
              />
            </label>
            {!markerColor && (
              <span data-testid="marker-tol-hint" className="faint mask-editor__hint">
                Pick a marker colour first (use the eyedropper)
              </span>
            )}
            {/* WS-I: live on the DEFAULT path too — with no colour picked this is the AUTO
                chroma key's coverage, which is the plane that now bakes. */}
            {keyedPct !== null && (
              <span
                data-testid="marker-keyed-pct"
                title={markerColor
                  ? "Share of the image this colour + tolerance keys to transparency (the print/transmission area). Raise the tolerance to pull in more nearby shades; drifting shades key partially."
                  : "Share of the image the automatic key turns transparent (the print/transmission area) — the same plane the bake's print mask comes from."}
                className="mask-editor__marker-keyed"
              >
                Keyed {keyedPct}%
              </span>
            )}
            {markerColor && (
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => {
                  setMarkerColor(null);
                  setMarkerEyedrop(false);
                  setMarkerTol(0);
                }}
                disabled={locked}
                title="Reset to the default keying"
                data-testid="marker-reset"
              >
                Reset to magenta
              </button>
            )}
          </div>
          {pickNotice && (
            <span
              role="status"
              data-testid="marker-pick-notice"
              className="faint mask-editor__hint"
            >
              {pickNotice}
            </span>
          )}
          <GuideKeyControls locked={locked} guideKey={guideKey} setGuideKey={setGuideKey} />
          {cameraShading}
          {/* Aurora: reassure the operator that background detection is local-only. */}
          <p className="mask-editor__ondevice mask-editor__hint--quiet" data-testid="chroma-ondevice"
            title="Background detection runs on-device — no cloud, no upload. Your image never leaves this machine.">
            Runs on-device — your image never leaves this machine.
          </p>
        </div>
    </>
  );
}
